import React, { useState, useMemo, useCallback, useEffect } from 'react';
import Swal from 'sweetalert2';
import EventsHeader from './EventsHeader';
import EventsFilters from './EventsFilters';
import EventsGrid from './EventsGrid';
import EventViewModal from './EventViewModal';
import EventFormModal from './EventFormModal';
import { getData, postData, uploadFile } from '../../../../services/apiService';
import Cookies from 'js-cookie';
import CryptoJS from 'crypto-js';

// Custom debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const EventsManagement = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dropdownLoading, setDropdownLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dropdownError, setDropdownError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [viewingEvent, setViewingEvent] = useState(null);
  const [searchInput, setSearchInput] = useState(''); // User input
  const [searchTerm, setSearchTerm] = useState(''); // Debounced search term for API
  const [activeFilter, setActiveFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('ASC');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [selectedModes, setSelectedModes] = useState([]);
  const [activeStatusList, setActiveStatusList] = useState([]);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [userRole, setUserRole] = useState(null); // State for user role
  const [selectedInstituteCategory, setSelectedInstituteCategory] = useState('');
  // State for dropdown data
  const [eventTypes, setEventTypes] = useState([]);
  const [eventModes, setEventModes] = useState([]);
  const [participantTypes, setParticipantTypes] = useState([]);
  const [institutes, setInstitutes] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [organizers, setOrganizers] = useState([]);
  const [guestDesignations, setGuestDesignations] = useState([]);


  const [formData, setFormData] = useState({
    Event_Name: '',
    Event_Sname: '',
    Inst_Name: '',
    Div_Name: '',
    Dept_id: '',
    Dept_Catg: '',
    Event_type: '',
    Event_mode: '',
    Participant_type: [],
    participant_pricing: [],
    guests: [],
    Start_datetime: '',
    End_datetime: '',
    PaymentStartDate: '',
    PaymentEndDate: '',
    Event_Place: '',
    GPS_coordinates: '',
    Event_status: "1",
    Organizer_id: "",
    Registration_Link: '',
    Event_Description: '',
    Message: '',
    is_group_event: 'no',
    event_group_type: '',
    max_group_size: '',
    LogoPath_Url: ''
  });

  // Secret key for decryption - use the same key that was used for encryption
  // Function to decrypt cookie value
  const decryptCookie = (encryptedValue) => {
    if (!encryptedValue) return null;
    try {
      const encryptionKey = process.env.REACT_APP_ENCRYPTION_KEY || 'your-secret-key';
      const bytes = CryptoJS.AES.decrypt(encryptedValue, encryptionKey);
      return bytes.toString(CryptoJS.enc.Utf8);
    } catch (error) {
      console.error('Error decrypting cookie:', error);
      return null;
    }
  };

  // Get and decrypt cookies on component mount
  useEffect(() => {
    // Get encrypted cookie
    const encryptedFrom = Cookies.get('from');
    // Decrypt the cookie
    if (encryptedFrom) {
      const decryptedFrom = decryptCookie(encryptedFrom);
      setUserRole(decryptedFrom);
    }
  }, []);

  // Debounce the search input
  const debouncedSearchTerm = useDebounce(searchInput, 500);

  // Update searchTerm when debounced value changes
  useEffect(() => {
    setSearchTerm(debouncedSearchTerm);
  }, [debouncedSearchTerm]);

  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const response = await getData('/Dropdown/GetActiveStatus');

        if (!response?.isError && response?.result) {
          setActiveStatusList(response.result);
        } else {
          setActiveStatusList([]);
        }
      } catch (error) {
        console.error('Error fetching active status:', error);
        setActiveStatusList([]);
      }
    };

    fetchDropdownData();
  }, []);

  const processApiResponse = (response, defaultData = []) => {
    if (!response) return defaultData;

    if (response.isError === false && response.data && Array.isArray(response.data)) {
      return response.data;
    }

    if (response.IsError === false && response.Result && Array.isArray(response.Result)) {
      return response.Result;
    }

    if (response.isError === false && response.result && Array.isArray(response.result)) {
      return response.result;
    }

    if (Array.isArray(response)) {
      return response;
    }

    return defaultData;
  };

  const fetchEventById = useCallback(async (eventId) => {
    try {
      const response = await getData(`Event/EventFullDetails/${eventId}`);


      if (response && (response.isError === false || response.IsError === false)) {
        return response.data || response.Data || response;
      } else {
        throw new Error(response?.message || response?.Message || 'Failed to fetch event details');
      }
    } catch (err) {
      console.error('Error fetching event:', err);
      throw err;
    }
  }, []);

  const fetchEventTypesByGroup = useCallback(async (isGroupEvent) => {
    try {
      const groupType = isGroupEvent ? 'Yes' : 'No';
      const response = await getData(`Dropdown/GetActiveEventType?groupType=${groupType}`);

      const eventTypesData = processApiResponse(response);
      if (eventTypesData.length > 0) {
        const formattedEventTypes = eventTypesData.map(type => ({
          id: type.EveType_ID || type.EveType_Name,
          name: type.EveType_Name,
          is_group_event: type.is_group_event || (groupType === 'Yes'),
          ...type
        }));
        setEventTypes(formattedEventTypes);
      } else {
        setEventTypes([]);
      }
    } catch (err) {
      console.error('Error fetching event types:', err);
      setEventTypes([]);
    }
  }, []);

  const fetchDropdownData = useCallback(async () => {
    try {
      setDropdownLoading(true);
      setDropdownError(null);

      const [
        institutesResponse,
        divisionsResponse,
        eventModesResponse,
        participantTypesResponse,
        organizersResponse,
        guestDesignationsResponse
      ] = await Promise.all([
        getData('Dropdown/GetActiveInstitutes'),
        getData('Dropdown/GetActiveDivision'),
        getData('Dropdown/GetActiveEventMode'),
        getData('Dropdown/GetActiveParticipant'),
        getData('Event/GetOrganizer'),
        getData('Dropdown/GetGuestDesignations')
      ]);

      // Process institute data
      const institutesData = processApiResponse(institutesResponse);
      if (institutesData.length > 0) {
        const formattedInstitutes = institutesData.map(inst => ({
          id: inst.Inst_Id,
          name: inst.Inst_Name,
          ...inst
        }));
        setInstitutes(formattedInstitutes);
      }

      // Process division data
      const divisionsData = processApiResponse(divisionsResponse);
      if (divisionsData.length > 0) {
        const formattedDivisions = divisionsData.map(div => ({
          id: div.Div_Id || div.Div_Name,
          name: div.Div_Name,
          ...div
        }));
        setDivisions(formattedDivisions);
      }

      // Process event mode data
      const eventModesData = processApiResponse(eventModesResponse);
      if (eventModesData.length > 0) {
        const formattedEventModes = eventModesData.map(mode => ({
          id: mode.EveMode_ID || mode.EveMode_ID,
          name: mode.EveMode_Name,
          ...mode
        }));
        setEventModes(formattedEventModes);
      }

      // Process participant type data
      const participantTypesData = processApiResponse(participantTypesResponse);
      if (participantTypesData.length > 0) {
        const formattedParticipantTypes = participantTypesData.map(participant => ({
          id: participant.Participant_ID || participant.Participant_ID,
          name: participant.Participant_Name,
          ...participant
        }));
        setParticipantTypes(formattedParticipantTypes);
      }

      // Process organizers data
      const organizersData = processApiResponse(organizersResponse);
      if (organizersData.length > 0) {
        const formattedOrganizers = organizersData.map(org => ({
          id: org.organizer_id || org.id,
          name: org.organizer_name || org.name,
          ...org
        }));
        setOrganizers(formattedOrganizers);
      }

      // Process guest designations data
      const guestDesignationsData = processApiResponse(guestDesignationsResponse);
      if (guestDesignationsData.length > 0) {
        const formattedGuestDesignations = guestDesignationsData.map(designation => ({
          id: designation.GUS_Desi_ID,
          name: designation.GUS_Desi_Name,
          ...designation
        }));
        setGuestDesignations(formattedGuestDesignations);
      }

      await fetchEventTypesByGroup(false);

      const responses = [
        institutesResponse,
        divisionsResponse,
        eventModesResponse,
        participantTypesResponse,
        organizersResponse,
        guestDesignationsResponse
      ];

      const errorResponses = responses.filter(res => res && (res.isError === true || res.IsError === true));
      if (errorResponses.length > 0) {
        const errorMessages = errorResponses.map(res => res.message || res.Message).join(', ');
        setDropdownError(`Some dropdown data failed to load: ${errorMessages}`);
      }

    } catch (err) {
      console.error('Error fetching dropdown data:', err);
      setDropdownError('Failed to load dropdown data. Please try again later.');
    } finally {
      setDropdownLoading(false);
    }
  }, [fetchEventTypesByGroup]);

  // SINGLE SELECT: Handle type toggle
  const handleTypeToggle = useCallback((typeId) => {
    const typeStr = typeId.toString();
    setSelectedTypes(prev => {
      if (prev.includes(typeStr)) {
        return []; // Deselect if clicking the same type
      }
      return [typeStr]; // Select only this type
    });
  }, []);

  // SINGLE SELECT: Handle mode toggle
  const handleModeToggle = useCallback((modeId) => {
    const modeStr = modeId.toString();
    setSelectedModes(prev => {
      if (prev.includes(modeStr)) {
        return []; // Deselect if clicking the same mode
      }
      return [modeStr]; // Select only this mode
    });
  }, []);

  // Clear all type filters
  const clearTypeFilters = useCallback(() => {
    setSelectedTypes([]);
  }, []);

  // Clear all mode filters
  const clearModeFilters = useCallback(() => {
    setSelectedModes([]);
  }, []);

  const buildQueryParams = useCallback(() => {
    const params = new URLSearchParams();

    if (sortBy === 'date') {
      params.append('sortOrder', sortOrder);
    } else if (sortBy === 'name') {
      params.append('eventName', searchTerm || '');
      params.append('sortOrder', sortOrder);
    }

    if (activeFilter !== 'all') {
      params.append('eventStatus', activeFilter);
    }

    if (searchTerm) {
      params.append('eventName', searchTerm);
    }

    // SINGLE SELECT: Only send the first (and only) selected type
    if (selectedTypes.length > 0) {
      params.append('eventType', selectedTypes[0]);
    }

    // SINGLE SELECT: Only send the first (and only) selected mode
    if (selectedModes.length > 0) {
      params.append('eventMode', selectedModes[0]);
    }

    return params.toString();
  }, [sortBy, sortOrder, activeFilter, searchTerm, selectedTypes, selectedModes]);

  const fetchEvents = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const queryParams = buildQueryParams();
      const url = queryParams ? `Event/GetEvents?${queryParams}` : 'Event/GetEvents';

      const response = await getData(url);

      const eventsData = processApiResponse(response);

      if (eventsData && eventsData.length > 0) {
        setEvents(eventsData);
      } else {
        setEvents([]);
        console.warn('No events data found in response');
      }
    } catch (err) {
      console.error('Error fetching events:', err);
      setError('Failed to load events. Please try again later.');
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [buildQueryParams]);

  useEffect(() => {
    fetchDropdownData().then(() => {
      fetchEvents();
    });
  }, [fetchDropdownData, fetchEvents]);

  useEffect(() => {
    if (showModal && formData.is_group_event) {
      fetchEventTypesByGroup(formData.is_group_event === 'yes');
    }
  }, [showModal, formData.is_group_event, fetchEventTypesByGroup]);

  // Refetch events when filters change (excluding searchInput)
  useEffect(() => {
    fetchEvents();
  }, [searchTerm, activeFilter, sortBy, sortOrder, selectedTypes, selectedModes, fetchEvents]);

  // SINGLE SELECT: Client-side filtering for single select
  const { filteredEvents, sortedEvents } = useMemo(() => {
    let filtered = Array.isArray(events) ? events : [];

    // SINGLE SELECT: Only filter if exactly one type is selected
    if (selectedTypes.length === 1) {
      filtered = filtered.filter(event =>
        event.Event_type?.toString() === selectedTypes[0]
      );
    }

    // SINGLE SELECT: Only filter if exactly one mode is selected
    if (selectedModes.length === 1) {
      filtered = filtered.filter(event =>
        event.Event_mode?.toString() === selectedModes[0]
      );
    }

    const sorted = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'participants':
          return (b.Max_participant || 0) - (a.Max_participant || 0);
        case 'featured':
          return (b.featured ? 1 : 0) - (a.featured ? 1 : 0);
        default:
          return 0;
      }
    });

    if (sortBy === 'participants' || sortBy === 'featured') {
      if (sortOrder === 'DESC') {
        sorted.reverse();
      }
    }

    return { filteredEvents: filtered, sortedEvents: sorted };
  }, [events, selectedTypes, selectedModes, sortBy, sortOrder]);

  const refreshEvents = useCallback(async () => {
    await fetchEvents();
    Swal.fire({
      title: 'Refreshed!',
      text: 'Events list has been updated',
      icon: 'success',
      timer: 2000,
      showConfirmButton: false,
      position: 'top-end',
      toast: true
    });
  }, [fetchEvents]);

  const refreshAllData = useCallback(async () => {
    await Promise.all([fetchDropdownData(), fetchEvents()]);
    Swal.fire({
      title: 'Refreshed!',
      text: 'All data has been updated',
      icon: 'success',
      timer: 2000,
      showConfirmButton: false,
      position: 'top-end',
      toast: true
    });
  }, [fetchDropdownData, fetchEvents]);

  const resetForm = useCallback(() => {
    setFormData({
      Event_Name: '',
      Event_Sname: '',
      Inst_Name: '',
      Div_Name: '',
      Dept_id: '',
      Event_type: '',
      Event_mode: '',
      Participant_type: [],
      participant_pricing: [],
      guests: [],
      Start_datetime: '',
      End_datetime: '',
      PaymentStartDate: '',
      PaymentEndDate: '',
      Event_Place: '',
      GPS_coordinates: '',
      Event_status: "1",
      Organizer_id: "",
      Registration_Link: '',
      Event_Description: '',
      Message: '',
      is_group_event: 'no',
      event_group_type: '',
      max_group_size: '',
      LogoPath_Url: ''
    });
  }, [])

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    try {
      // Check if this is a sub-event enabled event
      const hasSubEvents = formData.has_sub_events === 'yes' &&
        formData.sub_events &&
        formData.sub_events.length > 0;

      let response;

      if (hasSubEvents) {
        // Use SubEventFull-Details API for events with sub-events
        const subEventData = {
          Event_Id: editingEvent ? editingEvent.Event_Id || 0 : 0,
          Event_Name: formData.Event_Name || '',
          Event_Sname: formData.Event_Sname || '',
          Inst_Id: parseInt(formData.Inst_Name) || 0,
          Dept_id: parseInt(formData.Dept_id) || 0,
          Event_type: parseInt(formData.Event_type) || 0,
          Event_mode: parseInt(formData.Event_mode) || 0,
          Event_Grp_ID: parseInt(formData.event_group_type) || 0,
          IsGroupEvent: formData.is_group_event === 'yes' ? "Y" : "N",
          Max_Participant: parseInt(formData.max_group_size) || 0,
          Pay_Start_datetime: formData.PaymentStartDate || '',
          Pay_End_datetime: formData.PaymentEndDate || '',
          Start_datetime: formData.Start_datetime || '',
          End_datetime: formData.End_datetime || '',
          Event_Place: formData.Event_Place || '',
          GPS_coordinates: formData.GPS_coordinates || '',
          Event_status: parseInt(formData.Event_status) || 1,
          Organizer_id: parseInt(formData.Organizer_id) || 0,
          Event_Description: formData.Event_Description || '',
          HasSubEvents: "1",
          LogoPath_Url: formData.LogoPath_Url || '',
          // Sub-events specific data
          SubEventDetails: (formData.sub_events || []).map(subEvent => {
            const SubEventName = subEvent.Event_Name;
            const subEventId = subEvent.id || subEvent.Event_Id;
            const config = formData.sub_event_participants?.[subEventId.toString()] || {};

            return {
              SubEventId: parseInt(subEventId) || 0,
              Name: SubEventName,
              ShortName: "",
              Participants: (config.selectedParticipants || []).map(participantId => {
                const pricing = config.participantPricing?.[participantId] || {};
                const amount = parseFloat(pricing.amount) || 0;
                const gstPercent = 18;

                return {
                  Id: parseInt(participantId) || 0,
                  MaxParticipants: pricing.unlimited ? 0 : (parseInt(pricing.max_participants) || 0),
                  IsLimited: pricing.unlimited ? false : true,
                  Gst_Percents: gstPercent,
                  TotAmt: parseFloat(amount.toFixed(2))
                };
              })
            };
          })
        };

        response = await postData('Event/SaveEventWithSubEvents', subEventData);
      } else {
        // Use regular CreateEvent API for events without sub-events
        const EventDetails = (formData.participant_pricing || []).map(pricing => {
          const amount = parseFloat(pricing.amount) || 0;
          const gstPercent = 18;

          return {
            Id: parseInt(pricing.participant_id) || 0,
            MaxParticipants: pricing.unlimited ? 0 : (parseInt(pricing.max_participants) || 0),
            IsLimited: pricing.unlimited ? 0 : 1,
            Gst_Percents: gstPercent,
            TotAmt: parseFloat(amount.toFixed(2))
          };
        });

        const GuestDetails = (formData.guests || []).map((guest) => ({
          Guest_MobileNo: guest.Guest_Contact_Name || '',
          Guest_Name: guest.Guest_Name || '',
          Guest_Email: guest.Guest_Email || '',
          Designation: guest.Guest_designation || ''
        }));

        const Max_Participant = EventDetails.reduce((total, detail) => {
          return total + (detail.IsLimited === 1 ? detail.MaxParticipants : 0);
        }, 0);


        //Nitheesh
        const eventData = {
          Event_Id: editingEvent ? editingEvent.Event_Id || 0 : 0,
          Event_Name: formData.Event_Name || '',
          Event_Sname: formData.Event_Sname || '',
          Inst_Id: parseInt(formData.Inst_Name) || 0,
          Dept_id: parseInt(formData.Dept_id) || 0,
          Dept_Catg: parseInt(selectedInstituteCategory) || 0,
          Event_type: parseInt(formData.Event_type) || 0,
          Event_mode: parseInt(formData.Event_mode) || 0,
          Event_Grp_ID: parseInt(formData.event_group_type) || 0,
          IsGroupEvent: formData.is_group_event === 'yes' ? "Y" : "N",
          Max_Participant: parseInt(formData.max_group_size) || Max_Participant,
          Pay_Start_datetime: formData.PaymentStartDate || '',
          Pay_End_datetime: formData.PaymentEndDate || '',
          Start_datetime: formData.Start_datetime || '',
          End_datetime: formData.End_datetime || '',
          Event_Place: formData.Event_Place || '',
          GPS_coordinates: formData.GPS_coordinates || '',
          Event_status: parseInt(formData.Event_status) || 1,
          Organizer_id: parseInt(formData.Organizer_id) || 0,
          Event_Description: formData.Event_Description || '',
          EventDetails: EventDetails,
          GuestDetails: GuestDetails,
          Message: formData.Message || '',
          Registration_Link: formData.Registration_Link || '',
          LogoPath_Url: formData.LogoPath_Url || ''
        };

        response = await postData('Event/CreateEvent', eventData);
      }

      if (response && (response.isError === false || response.IsError === false)) {
        // Get the event ID from the response
        const eventId = response.eventId || response.data?.eventId ||
          response.Event_Id || editingEvent?.Event_Id;

        // Upload logo file if exists (only if a new file was uploaded)
        if (logoFile && eventId) {
          try {
            const formDataForUpload = new FormData();
            formDataForUpload.append('Event_Id', eventId);
            formDataForUpload.append('File', logoFile);

            const uploadResponse = await uploadFile('event-logo/upload', formDataForUpload, null, false);

            if (uploadResponse && (uploadResponse.isError === false || uploadResponse.IsError === false)) {
              Swal.fire({
                title: 'Logo uploaded successfully!',
                text: 'Logo uploaded successfully',
                icon: 'success',
                confirmButtonColor: '#3B82F6',
                confirmButtonText: 'OK',
              });
            } else {
              console.warn('Logo upload failed:', uploadResponse?.message);
              Swal.fire({
                title: 'Logo Upload Warning',
                text: 'Event was saved successfully but logo upload failed. You can try uploading it later.',
                icon: 'warning',
                confirmButtonText: 'OK',
              });
            }
          } catch (uploadError) {
            console.error('Error uploading logo:', uploadError);
            Swal.fire({
              title: 'Event saved, but logo upload failed',
              text: 'The event was created but the logo could not be uploaded. You can try uploading it later.',
              icon: 'warning',
              confirmButtonText: 'OK',
            });
          }
        }

        Swal.fire({
          title: 'Success!',
          text: hasSubEvents
            ? 'Event with sub-events created successfully'
            : (editingEvent ? 'Event updated successfully' : 'Event created successfully'),
          icon: 'success',
          confirmButtonColor: '#3B82F6',
          confirmButtonText: 'OK',
        });

        await fetchEvents();

        setShowModal(false);
        setEditingEvent(null);
        setLogoFile(null);
        setLogoPreview(null);
        resetForm();
      } else {
        Swal.fire({
          title: 'Error!',
          text: response?.message || response?.Message || 'Failed to save event',
          icon: 'error',
          confirmButtonColor: '#EF4444',
          confirmButtonText: 'OK',
        });
      }
    } catch (err) {
      console.error('Error saving event:', err);
      Swal.fire({
        title: 'Error!',
        text: 'Failed to save event. Please try again.',
        icon: 'error',
        confirmButtonColor: '#EF4444',
        confirmButtonText: 'OK',
      });
    }
  }, [editingEvent, formData, logoFile, fetchEvents, resetForm]);

  ;

  const handleEdit = useCallback(async (event) => {
    if (!event || !event.Event_Id) return;

    try {
      Swal.fire({
        title: 'Loading...',
        text: 'Fetching event details',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      const response = await fetchEventById(event.Event_Id);

      if (!response || response.isError === true || response.IsError === true) {
        throw new Error('Failed to fetch event details');
      }

      const eventData = response.data || response;

      if (!eventData) {
        throw new Error('No event data received');
      }

      Swal.close();

      // FIRST: Get the institute ID to determine category
      const selectedInstituteId = eventData.Inst_Id?.toString();
      let instituteCategory = '';

      if (selectedInstituteId && institutes.length > 0) {
        const selectedInstitute = institutes.find(inst =>
          inst.id?.toString() === selectedInstituteId ||
          inst.Inst_Id?.toString() === selectedInstituteId
        );
        instituteCategory = selectedInstitute?.Category || selectedInstitute?.category || '';
        setSelectedInstituteCategory(instituteCategory);
      }

      // Wait for department dropdown to load if needed
      if (instituteCategory) {
        // Give a small delay to ensure department dropdown is loaded
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      const participantTypeIds = (eventData.Participants || []).map(p =>
        p.Participant_TypeId?.toString() || ''
      ).filter(id => id);

      const participantPricing = (eventData.Participants || []).map(participant => ({
        participant_id: participant.Participant_TypeId?.toString() || '',
        amount: participant.TotAmt?.toString() || '',
        max_participants: participant.Max_participant?.toString() || '',
        unlimited: participant.IsLimited === 0 // FIXED: 0 = unlimited, 1 = limited
      }));

      const guests = (eventData.Guests || []).map((guest, index) => ({
        id: Date.now() + index,
        Guest_Name: guest.Guest_Name || '',
        Guest_Contact_Name: guest.Guest_MobileNo || '',
        Guest_Email: guest.Guest_Email || '',
        Guest_designation: guest.Designation || ''
      }));

      // FIX: Properly check IsGroupEvent from API response
      const isGroupEvent =
        eventData.IsGroupEvent === "Y" ||
        eventData.IsGroupEvent === "Yes" ||
        eventData.is_group_event === true ||
        eventData.is_group_event === "yes" ||
        eventData.IsGroupEvent === "y";

      await fetchEventTypesByGroup(isGroupEvent);

      const formDataObj = {
        Event_Name: eventData.Event_Name || '',
        Event_Sname: eventData.Event_Sname || '',
        Inst_Name: eventData.Inst_Id?.toString() || '',
        Div_Name: eventData.Div_id?.toString() || '',
        Dept_id: eventData.Dept_id?.toString() || '', // Convert to string
        Event_type: eventData.Event_type?.toString() || '',
        Event_mode: eventData.Event_mode?.toString() || '',
        Participant_type: participantTypeIds,
        participant_pricing: participantPricing,
        guests: guests.length > 0 ? guests : [{
          id: Date.now(),
          Guest_Name: '',
          Guest_Contact_Name: '',
          Guest_Email: '',
          Guest_designation: ''
        }],
        Start_datetime: eventData.Start_datetime || '',
        End_datetime: eventData.End_datetime || '',
        PaymentStartDate: eventData.Pay_Start_datetime || '',
        PaymentEndDate: eventData.Pay_End_datetime || '',
        Event_Place: eventData.Event_Place || '',
        GPS_coordinates: eventData.GPS_coordinates || '',
        Event_status: eventData.Event_status?.toString() || '1',
        Organizer_id: eventData.Organizer_id?.toString() || '',
        Registration_Link: eventData.Registration_Link || '',
        Event_Description: eventData.Event_Description || '',
        Message: eventData.Message || '',
        is_group_event: isGroupEvent ? 'yes' : 'no',
        event_group_type: eventData.Event_Grp_ID?.toString() || '',
        max_group_size: eventData.Max_Participant?.toString() || '',
        LogoPath_Url: eventData.LogoPath_Url || ''
      };

      // If there's a logo URL, set it for preview
      if (eventData.LogoPath_Url) {
        setLogoPreview(eventData.LogoPath_Url);
      } else {
        setLogoPreview(null);
      }

      setEditingEvent(eventData);
      setFormData(formDataObj);
      setLogoFile(null); // Reset logo file when editing
      setShowModal(true);

    } catch (err) {
      console.error('Error fetching event details:', err);
      Swal.close();

      Swal.fire({
        title: 'Error!',
        text: 'Failed to load event details. Please try again.',
        icon: 'error',
        confirmButtonColor: '#EF4444',
        confirmButtonText: 'OK',
      });
    }
  }, [fetchEventById, fetchEventTypesByGroup, institutes, setSelectedInstituteCategory]);

  const handleView = useCallback(async (event) => {
    if (!event || !event.Event_Id) return;

    try {
      Swal.fire({
        title: 'Loading...',
        text: 'Fetching event details',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      const response = await fetchEventById(event.Event_Id);

      if (!response || response.isError === true || response.IsError === true) {
        throw new Error('Failed to fetch event details');
      }

      const eventData = response.data || response;

      if (!eventData) {
        throw new Error('No event data received');
      }

      Swal.close();

      setViewingEvent(eventData);
      setShowViewModal(true);

    } catch (err) {
      console.error('Error fetching event details for view:', err);
      Swal.close();

      Swal.fire({
        title: 'Error!',
        text: 'Failed to load event details. Please try again.',
        icon: 'error',
        confirmButtonColor: '#EF4444',
        confirmButtonText: 'OK',
      });
    }
  }, [fetchEventById]);

  const handleDelete = useCallback(async (event) => {
    if (!event) return;

    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete "${event.Event_Name}". This action cannot be undone!`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#EF4444',
      cancelButtonColor: '#6B7280',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
    });

    if (result.isConfirmed) {
      try {
        await postData('Event/DeleteEvent', { Event_Id: event.Event_Id });

        setEvents(prev => prev.filter(e => e.Event_Id !== event.Event_Id));

        Swal.fire({
          title: 'Deleted!',
          text: 'Event has been deleted successfully',
          icon: 'success',
          confirmButtonColor: '#3B82F6',
          confirmButtonText: 'OK'
        });
      } catch (err) {
        console.error('Error deleting event:', err);
        Swal.fire({
          title: 'Error!',
          text: 'Failed to delete event',
          icon: 'error',
          confirmButtonColor: '#EF4444',
          confirmButtonText: 'OK'
        });
      }
    }
  }, []);

  const handleShare = useCallback((event) => {
    if (!event) return;
    const registrationUrl = `${event.Registration_Link}`;

    navigator.clipboard.writeText(registrationUrl);
    Swal.fire({
      title: 'Copied!',
      text: 'Registration link copied to clipboard',
      icon: 'success',
      timer: 2000,
      showConfirmButton: false,
      position: 'top-end',
      toast: true
    });
  }, []);

  const toggleFeatured = useCallback((eventId) => {
    setEvents(prev => prev.map(event =>
      event.Event_Id === eventId
        ? { ...event, featured: !event.featured }
        : event
    ));
  }, []);

  const toggleSortOrder = useCallback(() => {
    setSortOrder(prev => prev === 'ASC' ? 'DESC' : 'ASC');
  }, []);

  const clearAllFilters = useCallback(() => {
    setSearchInput('');
    setActiveFilter('all');
    setSelectedTypes([]);
    setSelectedModes([]);
    setSortOrder('ASC');
    setSortBy('date');
  }, []);

  // Handle search input change
  const handleSearchChange = useCallback((value) => {
    setSearchInput(value);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-4">
      <div className="max-w-7xl mx-auto">
        <EventsHeader
          onCreateEvent={() => setShowModal(true)}
          onRefreshEvents={refreshAllData}
          loading={loading || dropdownLoading}
          userRole={userRole}
        />

        {dropdownLoading && (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <span className="ml-3 text-gray-600">Loading dropdown data...</span>
          </div>
        )}

        {loading && !dropdownLoading && (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            <span className="ml-3 text-lg text-gray-600">Loading events...</span>
          </div>
        )}

        {error && !loading && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <div className="flex items-center">
              <div className="text-red-500 mr-2">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <span className="text-red-800">{error}</span>
              <button
                onClick={refreshEvents}
                className="ml-auto bg-red-100 hover:bg-red-200 text-red-800 px-3 py-1 rounded text-sm font-medium transition-colors"
              >
                Retry
              </button>
            </div>
          </div>
        )}

        {dropdownError && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
            <div className="flex items-center">
              <div className="text-yellow-500 mr-2">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <span className="text-yellow-800">{dropdownError}</span>
              <button
                onClick={fetchDropdownData}
                className="ml-auto bg-yellow-100 hover:bg-yellow-200 text-yellow-800 px-3 py-1 rounded text-sm font-medium transition-colors"
              >
                Retry
              </button>
            </div>
          </div>
        )}

        <>
          <EventsFilters
            searchTerm={searchInput} // Use searchInput instead of searchTerm
            onSearchChange={handleSearchChange} // Use the new handler
            activeFilter={activeFilter}
            onFilterChange={setActiveFilter}
            sortBy={sortBy}
            onSortChange={setSortBy}
            sortOrder={sortOrder}
            onSortOrderChange={toggleSortOrder}
            showFilters={showFilters}
            onToggleFilters={() => setShowFilters(!showFilters)}
            selectedTypes={selectedTypes}
            onTypeToggle={handleTypeToggle}
            selectedModes={selectedModes}
            onModeToggle={handleModeToggle}
            eventTypes={eventTypes}
            eventModes={eventModes}
            eventStatuses={activeStatusList}
            onClearFilters={clearAllFilters}
            clearTypeFilters={clearTypeFilters}
            clearModeFilters={clearModeFilters}
          />

          <EventsGrid
            events={sortedEvents}
            onView={handleView}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onShare={handleShare}
            onToggleFeatured={toggleFeatured}
            searchTerm={searchInput}
            activeFilter={activeFilter}
            selectedTypes={selectedTypes}
            selectedModes={selectedModes}
            onClearFilters={clearAllFilters}
            onCreateEvent={() => setShowModal(true)}
            eventTypes={eventTypes}
            eventModes={eventModes}
            participantTypes={participantTypes}
            eventStatuses={activeStatusList}
            userRole={userRole} // Pass user role to grid if needed
          />
        </>

        <EventViewModal
          isOpen={showViewModal}
          event={viewingEvent}
          onClose={() => setShowViewModal(false)}
          onEdit={() => {
            setShowViewModal(false);
            handleEdit(viewingEvent);
          }}
          onShare={handleShare}
          eventTypes={eventTypes}
          eventModes={eventModes}
          participantTypes={participantTypes}
          eventStatuses={activeStatusList}
          userRole={userRole} // Pass user role to modal if needed
        />

        <EventFormModal
          logoFile={logoFile}
          setLogoFile={setLogoFile}
          isOpen={showModal}
          editingEvent={editingEvent}
          formData={formData}
          selectedInstituteCategory={selectedInstituteCategory}
          setSelectedInstituteCategory={setSelectedInstituteCategory}
          logoPreview={logoPreview}
          setLogoPreview={setLogoPreview}
          onFormDataChange={setFormData}
          onSubmit={handleSubmit}
          onClose={() => {
            setShowModal(false);
            setEditingEvent(null);
            setLogoFile(null);
            setLogoPreview(null);
            resetForm();
          }}
          eventTypes={eventTypes}
          eventModes={eventModes}
          participantTypes={participantTypes}
          institutes={institutes}
          divisions={divisions}
          organizers={organizers}
          guestDesignations={guestDesignations}
          eventStatuses={activeStatusList}
          userRole={userRole} // Pass user role to form modal if needed
        />
      </div>
    </div>
  );
};

export default EventsManagement;